import { inArray } from '@teamworksdev/loco-sync-client';
import { Typography } from '@mui/material';
import { athleteDisplayName } from '@strength/domain';
import { mergeAthleteWorkoutLayers, Workout } from '@strength/domain-workout';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '../../../../loco-sync';
import { cn } from '../../../components/utils';
import { excludePlaceholderLayers, getOptionsFromWorkouts } from '../utils';
import { ActionBar } from './components/ActionBar';
import { MenuBar } from './components/MenuBar';
import { SectionExercises } from './components/SectionExercises';
import { WorkoutOverview } from './components/WorkoutOverview';
import { WorkoutSelection } from './components/WorkoutSelection';
import { useTabletContext } from './context/TabletContext';
import { WorkoutProvider } from './context/WorkoutContext';
import {
  categorizeWorkoutsByDate,
  getUnrecordedActuals,
} from './group-workout-utils';
import {
  AthleteTabletWorkoutFlowProps,
  TabletExerciseFilters,
  WorkoutData,
} from './types';

export const TabletActualsCollection = () => {
  const {
    selectedAthletes,
    getSelectedWorkoutId,
    getSelectedSectionId,
    athleteSession,
    activeAthletes,
    splitViewEnabled,
    setActiveAthletes,
    onExit,
  } = useTabletContext();
  const { t } = useTranslation();

  // Exercise/Set Filter state
  const [filters, setFilters] = useState<TabletExerciseFilters>({
    setFilter: 'all-sets',
    exerciseFilters: [],
  });

  // Query for all athlete workout layers for the selected athletes
  // All selected athletes not just active athletes in order to handle exercise filters
  // Load block with nested track relationship for team filtering
  const { data: athleteLayersRaw } = useQuery(
    'AthletePublishedWorkoutLayer',
    {
      isCurrent: true,
      profileId: inArray(selectedAthletes.map((a) => a.id)),
      status: inArray(['planned', 'completed', 'skipped']),
    },
    {
      block: { track: {} },
      actual: {},
      modifier: {},
      draft: {},
      blockDraft: {},
    },
  );

  // Filter layers by team before querying block layers to avoid fetching unnecessary data
  // Also dedupe isCurrent in case we have multiple (SC-3662)
  const filteredAthleteLayers = useMemo(() => {
    const athleteTeamMap = new Map(
      selectedAthletes.map((a) => [a.id, a.teamId]),
    );

    const latestVersionByWorkoutId = athleteLayersRaw.reduce(
      (map, next) => {
        const savedVersion = map[`${next.profileId}:${next.workoutId}`] ?? 0;
        if (next.version > savedVersion) {
          map[`${next.profileId}:${next.workoutId}`] = next.version;
        }
        return map;
      },
      {} as Record<string, number>,
    );

    const teamFiltered = athleteLayersRaw.filter((layer) => {
      const layerTeamId = layer.block?.track?.teamId;
      const expectedTeamId = athleteTeamMap.get(layer.profileId);
      const latestVersion =
        latestVersionByWorkoutId[`${layer.profileId}:${layer.workoutId}`];
      return layerTeamId === expectedTeamId && layer.version === latestVersion;
    });

    return excludePlaceholderLayers(teamFiltered);
  }, [athleteLayersRaw, selectedAthletes]);

  const { data: blockLayers } = useQuery('BlockPublishedWorkoutLayer', {
    isCurrent: true,
    workoutId: inArray(filteredAthleteLayers.map((a) => a.workoutId)),
  });

  const athleteLayers = useMemo(() => {
    return filteredAthleteLayers.map((layer) => ({
      ...layer,
      blockLayer: blockLayers.find(
        (block) => block.workoutId === layer.workoutId,
      ),
    }));
  }, [filteredAthleteLayers, blockLayers]);

  // Get WorkoutData for an athlete categorized by date
  const getAthleteCategorizedWorkouts = useCallback(
    (athleteId: string) => {
      if (!athleteId || !athleteLayers)
        return { today: [], missing: [], upcoming: [], flex: [] };

      const athleteWorkouts = athleteLayers.filter(
        (w) => w.profileId === athleteId,
      );

      return categorizeWorkoutsByDate(athleteWorkouts);
    },
    [athleteLayers],
  );

  // Get workout layer for an athlete
  const getAthleteWorkoutLayers = useCallback(
    (athleteId: string) => {
      const workoutId = athleteSession[athleteId]?.workoutId;
      if (!athleteLayers || !workoutId) return undefined;
      return athleteLayers.find(
        (w) => w.profileId === athleteId && w.workoutId === workoutId,
      );
    },
    [athleteLayers, athleteSession],
  );

  // Workout layers for ALL selected athletes
  const selectedAthletesWorkoutLayers: WorkoutData[] | undefined =
    useMemo(() => {
      if (!athleteLayers || selectedAthletes.length === 0) return undefined;

      return selectedAthletes
        .map((athlete) => {
          const workoutId = athleteSession[athlete.id]?.workoutId;
          if (!workoutId) return undefined;

          return athleteLayers.find(
            (w) => w.profileId === athlete.id && w.workoutId === workoutId,
          );
        })
        .filter((workout) => workout !== undefined) as WorkoutData[];
    }, [athleteLayers, selectedAthletes, athleteSession]);

  const [athletesWithUnrecordedActuals, setUnrecordedActuals] = useState<
    string[]
  >([]);

  // Clean up exercise filters in state when available exercise options change
  // Remove any selected exercises that are no longer available in the current workouts
  // Track athletes who have unrecorded actuals
  useLayoutEffect(() => {
    if (selectedAthletesWorkoutLayers) {
      const workouts = selectedAthletesWorkoutLayers
        .map((layers) => {
          layers.profileId;
          return {
            profileId: layers.profileId,
            workout: mergeAthleteWorkoutLayers(
              layers.blockLayer,
              layers,
              layers.modifier,
              layers.actual,
            ),
          };
        })
        .filter(
          (
            workoutData,
          ): workoutData is {
            profileId: string;
            workout: NonNullable<Workout>;
          } => workoutData.workout !== undefined,
        );

      const missingActualsByAthleteId = workouts.flatMap((w) => {
        if (getUnrecordedActuals(w.workout).length > 0) {
          return [w.profileId];
        }
        return [];
      });

      setUnrecordedActuals(missingActualsByAthleteId);

      if (filters.exerciseFilters.length > 0) {
        const exerciseOptions = getOptionsFromWorkouts(workouts, t);

        if (exerciseOptions?.exerciseOptions) {
          const availableExerciseIds = new Set(
            exerciseOptions.exerciseOptions.map(
              (option) => option.libraryExerciseId,
            ),
          );
          const validExercises = filters.exerciseFilters.filter((exercise) =>
            availableExerciseIds.has(exercise.id),
          );
          if (validExercises.length !== filters.exerciseFilters.length) {
            setFilters((prev) => ({
              ...prev,
              exerciseFilters: validExercises,
            }));
          }
        }
      }
    } else {
      setUnrecordedActuals([]);
    }
  }, [selectedAthletesWorkoutLayers, filters.exerciseFilters, t]);

  // Handler for switching athletes in single view (via MenuBar)
  const handleSelectAthlete = (athleteId: string) => {
    const athlete = selectedAthletes.find((a) => a.id === athleteId);
    if (athlete) {
      setActiveAthletes([athlete]);
    }
  };

  if (activeAthletes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <Typography variant="h4">{t('noAthletesSelected')}</Typography>
      </div>
    );
  }

  const athleteCount = selectedAthletes.length;
  const gridClass =
    athleteCount === 2
      ? 'grid grid-cols-2 grid-rows-1'
      : 'grid grid-cols-2 grid-rows-2';

  return (
    <div className="h-full flex flex-row w-full">
      {!splitViewEnabled && (
        <MenuBar
          setSelectedAthleteId={handleSelectAthlete}
          athletes={selectedAthletes}
        />
      )}
      <div className="overflow-hidden pt-2 flex-grow flex flex-col relative">
        <ActionBar
          onExit={onExit}
          athleteWorkoutLayers={athleteLayers}
          selectedAthletesWorkoutLayers={selectedAthletesWorkoutLayers}
          filters={filters}
          onFiltersChange={setFilters}
          unrecordedActuals={athletesWithUnrecordedActuals}
        />
        <div
          className={cn(
            `flex-grow ${gridClass} overflow-hidden`,
            splitViewEnabled && 'gap-2 p-2',
            splitViewEnabled ? gridClass : 'grid grid-cols-1 grid-rows-1',
          )}
        >
          {activeAthletes.map((athlete) => (
            <div
              key={athlete.id}
              className={cn(
                'overflow-hidden flex flex-col relative',
                splitViewEnabled && 'border border-slate-300 rounded-lg ',
              )}
            >
              <AthleteTabletWorkoutFlow
                athlete={athlete}
                hasAthleteSelectedSection={!!getSelectedSectionId(athlete.id)}
                hasAthleteSelectedWorkout={!!getSelectedWorkoutId(athlete.id)}
                athleteWorkoutLayers={getAthleteWorkoutLayers(athlete.id)}
                athleteCategorizedWorkouts={getAthleteCategorizedWorkouts(
                  athlete.id,
                )}
                filters={filters}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const AthleteTabletWorkoutFlow = ({
  athlete,
  hasAthleteSelectedSection,
  hasAthleteSelectedWorkout,
  athleteWorkoutLayers,
  athleteCategorizedWorkouts,
  filters,
}: AthleteTabletWorkoutFlowProps) => {
  return (
    <>
      <div className="h-[56px] bg-slate-50 border-y border-slate-200 flex items-center px-4 flex-shrink-0">
        <Typography variant="h3">{athleteDisplayName(athlete)}</Typography>
      </div>
      {!hasAthleteSelectedWorkout || !athleteWorkoutLayers ? (
        <WorkoutSelection
          athlete={athlete}
          athleteCategorizedWorkouts={athleteCategorizedWorkouts}
        />
      ) : (
        <WorkoutProvider
          athleteWorkoutLayers={athleteWorkoutLayers}
          selectedAthlete={athlete}
          filters={filters}
        >
          {hasAthleteSelectedSection ? (
            <SectionExercises />
          ) : (
            <WorkoutOverview />
          )}
        </WorkoutProvider>
      )}
    </>
  );
};
